<script setup lang="ts">
import { computed, ref, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useSelectionStore } from '@/stores/selection'
import { useJudgmentOverview, useInitMasters } from '@/api/queries'
import { useLayoutStore, type ColumnKey } from '@/stores/layout'
import { resolveStatusStyle } from '@/utils/judgmentStatus'
import StatusIcon from './StatusIcon.vue'
import type { JudgmentOverviewItem } from '@/types/api'

const selection = useSelectionStore()
const layout = useLayoutStore()
const params = computed(() => selection.effectiveParams)
const { data, isLoading, isError } = useJudgmentOverview(params)

const items = computed(() => data.value?.items ?? [])

// ---- 並べ替え ----
// 一覧はサーバー側で LIMIT 500 されるため、クライアントで並べ替えると
// 「取得済み500件の中での並び」になってしまう。並べ替えもサーバーに委ねる。
type SortKey =
  | 'judgmentStatus' | 'sellerId' | 'brand' | 'parentAsin' | 'childAsin'
  | 'productName' | 'color' | 'size' | 'createDate' | 'updateDate' | 'judgeDate'

function toggleSort(key: SortKey) {
  selection.toggleSort(key)
}

/** ヘッダーに出す並び順の記号 */
/**
 * 親ASINの「変わり目」を判定する。
 *
 * 同じ親ASINの商品(バリエーション違い)は連続して並ぶが、ASINは
 * 等幅フォントだと見た目が似ており、どこで親が切り替わったのか分かりにくい。
 * VB版は可変幅フォントで文字幅が変わるため区別できていたが、
 * フォントを変えると桁揃えが崩れて別の読みにくさが出る。
 * そこで「変わり目の行に区切り線を引く」方式にする。
 *
 * 親ASINでソートしていなくても、同じ親が隣り合えば同じグループとして扱う。
 */
function isParentBoundary(index: number): boolean {
  if (index === 0) return false
  const prev = items.value[index - 1]?.parentAsin
  const cur = items.value[index]?.parentAsin
  return prev !== cur
}

/** 同じ親ASINが2件以上連続する範囲か(単独商品には帯を出さない) */
function isInParentGroup(index: number): boolean {
  const cur = items.value[index]?.parentAsin
  if (!cur) return false
  return items.value[index - 1]?.parentAsin === cur
      || items.value[index + 1]?.parentAsin === cur
}

// ---- 列幅のリサイズ ----
// 項目名の境目をドラッグして、列の幅を変えられるようにする。
// 商品名など既定幅で切れてしまう項目を、その場で広げて確認できる。
// 幅は layout ストア経由で localStorage に保存され、商品を切り替えても維持される。
const resizing = ref<{ key: ColumnKey; startX: number; startW: number } | null>(null)

function onResizeStart(key: ColumnKey, e: PointerEvent) {
  // ヘッダーのクリック(ソート)と競合させない
  e.stopPropagation()
  e.preventDefault()
  ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  resizing.value = { key, startX: e.clientX, startW: layout.columnWidths[key] }
  document.body.style.userSelect = 'none'
  document.body.style.cursor = 'col-resize'
}

function onResizeMove(e: PointerEvent) {
  const r = resizing.value
  if (!r) return
  layout.setColumnWidth(r.key, r.startW + (e.clientX - r.startX))
}

function onResizeEnd(e: PointerEvent) {
  if (!resizing.value) return
  ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)
  resizing.value = null
  document.body.style.userSelect = ''
  document.body.style.cursor = ''
  layout.persist()
}

/** ダブルクリックでその列だけ既定幅に戻す…ではなく、全列まとめて戻す */
function onResizeDouble(e: MouseEvent) {
  e.stopPropagation()
  layout.resetColumnWidths()
}

/** grid-template-columns を列幅から組み立てる */
const gridStyle = computed(() => {
  const w = layout.columnWidths
  // 商品名も他列と同じくpx固定にする。全体の合計幅が画面より広くなった分は、
  // テーブル側の横スクロールで見られるようにする(1frのままだと
  // ドラッグしても実際の表示幅が変わらないため)。
  return {
    gridTemplateColumns:
      `24px ${w.status}px ${w.seller}px ${w.parentAsin}px ${w.childAsin}px ${w.brand}px ` +
      `${w.productName}px ${w.color}px ${w.size}px ` +
      `${w.createDate}px ${w.updateDate}px ${w.judgeDate}px`,
  }
})

function sortMark(key: SortKey): string {
  if (selection.sortKey !== key) return ''
  return selection.sortDir === 'asc' ? '▲' : '▼'
}

// ---- セルのダブルクリックで絞り込み ----
// 値をそのまま検索条件に入れる。セラーは検索キーが内部ID、表示は別名なので分けて渡す。
function filterBySeller(item: JudgmentOverviewItem) {
  selection.applyQuickFilter('sellerId', item.sellerId, formatSellerId(item.sellerId))
}
function filterByBrand(item: JudgmentOverviewItem) {
  if (item.brand) selection.applyQuickFilter('brand', item.brand)
}
function filterByParentAsin(item: JudgmentOverviewItem) {
  if (item.parentAsin) selection.applyQuickFilter('parentAsin', item.parentAsin)
}
/** 登録日をダブルクリック → その日1日で絞り込む */
function filterByCreateDate(item: JudgmentOverviewItem) {
  if (!item.createDate) return
  const day = String(item.createDate).slice(0, 10) // YYYY-MM-DD
  selection.filters = {
    ...selection.filters,
    createDateFrom: day,
    createDateTo: day,
  }
}

function filterByChildAsin(item: JudgmentOverviewItem) {
  if (item.childAsin) selection.applyQuickFilter('childAsin', item.childAsin)
}

// ---- ステータス表示 ----
// 名称は marketplace.mjudgmentstatus から取得する(VB.NET版も同じマスタを
// 動的に読んでコンボボックスを作っていた)。
// 色分けだけは主コード基準で固定する。
const { data: masters } = useInitMasters()

const statusNameMap = computed(() => {
  const map: Record<string, string> = {}
  for (const row of masters.value?.judgmentStatusMaster ?? []) {
    map[`${row.JudgmentStatusCode}${row.JudgmentSubStatusCode}`] = row.JudgmentStatusName
  }
  return map
})

function meta(item: JudgmentOverviewItem) {
  const key = `${item.judgmentStatus}${item.judgmentSubStatus}`
  const label =
    statusNameMap.value[key] ??
    statusNameMap.value[`${item.judgmentStatus}0`] ??
    item.judgmentStatus
  // 色・アイコンはフィルタのタブと同じ定義から解決する(見た目を必ず一致させる)
  const style = resolveStatusStyle(item.judgmentStatus, item.judgmentSubStatus)
  return { label, tone: style.tone, icon: style.icon }
}

// ---- キーボードでの明細移動 ----
// 審査は「選んで判定、次へ」の繰り返しなので、マウスに持ち替えず
// ↑↓で明細を送れるようにする。入力中は誤爆しないよう無効化する。
function onKeydown(e: KeyboardEvent) {
  if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return

  // テキスト入力やテキストエリアにフォーカスがあるときは通常のカーソル移動を優先
  const el = document.activeElement as HTMLElement | null
  if (el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable)) return

  const list = items.value
  if (list.length === 0) return

  const cur = activeIndex()
  // 未選択なら先頭から始める
  const next = cur < 0 ? 0 : cur + (e.key === 'ArrowDown' ? 1 : -1)
  if (next < 0 || next >= list.length) return

  e.preventDefault() // ページ全体のスクロールを止める
  const t = list[next]
  activeRow.value = next
  selection.select(t.sellerId, t.childAsin)

  // 選択行が画面外に出ないよう追従させる
  nextTick(() => {
    document
      .querySelector('.trow.grid-row.selected')
      ?.scrollIntoView({ block: 'nearest' })
  })
}

onMounted(() => window.addEventListener('keydown', onKeydown))
onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown))

// 同じ出品が親ASIN違いで複数行出ることがあるため(VB版と同じ)、
// どの行を選んだかを行番号で覚えておく(↑↓で1行ずつ送れるように・選んだ行だけ色を付けるように)
const activeRow = ref<number | null>(null)

function matchesSelection(item: JudgmentOverviewItem | undefined) {
  return !!item && selection.selectedAsin === item.childAsin && selection.selectedSellerId === item.sellerId
}

/** いま選んでいる行の番号(覚えている行が選択とずれていれば、最初に一致する行) */
function activeIndex(): number {
  const list = items.value
  if (activeRow.value !== null && matchesSelection(list[activeRow.value])) return activeRow.value
  return list.findIndex((i) => matchesSelection(i))
}

function selectRow(item: JudgmentOverviewItem, idx: number) {
  activeRow.value = idx
  selection.select(item.sellerId, item.childAsin)
}

function isSelected(item: JudgmentOverviewItem, idx: number) {
  return matchesSelection(item) && activeIndex() === idx
}

function rowKey(item: JudgmentOverviewItem, idx: number) {
  return `${item.sellerId}:${item.childAsin}:${item.parentAsin ?? ''}:${idx}`
}

function isChecked(item: JudgmentOverviewItem) {
  return selection.checkedKeys.has(`${item.sellerId}:${item.childAsin}`)
}

// 修正前:
// function shortDate(value: string | null): string {
//   if (!value) return '-'
//   const d = new Date(value)
//   if (Number.isNaN(d.getTime())) return value.slice(0, 10)
//   return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`
// }

// 修正後 (yy/mm/dd 形式):
function shortDate(value: string | null): string {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value.slice(0, 10)
  const yy = String(d.getFullYear()).slice(-2)
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yy}/${mm}/${dd}`
}

// セラーIDのプレフィックス付与関数を追加
function formatSellerId(id: string) {
  if (!id) return ''
  return id.startsWith('SSC-') ? id : `SSC-${id}`
}

// 全選択のチェックは、一覧のチェックの状態から決める(自分で状態を持たない)。
// 審査OK・保存などの後にチェックが外れると、全選択のチェックも一緒に外れる
const allChecked = computed(() => items.value.length > 0 && items.value.every((i) => isChecked(i)))
const someChecked = computed(() => !allChecked.value && items.value.some((i) => isChecked(i)))

function onSelectAll(e: Event) {
  const checked = (e.target as HTMLInputElement).checked
  selection.clearChecked()
  // 同じ出品が親ASIN違いで複数行あることがあるので、切り替えではなく「付ける」にする
  if (checked) items.value.forEach((i) => selection.setChecked(i.sellerId, i.childAsin))
}
</script>

<template>
  <div class="table-section">
    <div class="table-toolbar">
      <label class="chk"><input type="checkbox" :checked="allChecked" :indeterminate="someChecked" @change="onSelectAll"> 全選択</label>
      <span class="sel-count">{{ selection.checkedCount }}件選択中</span>
      <div class="spacer"></div>
    </div>

    <!-- 現在の絞り込み条件。個別に外して、残りの条件で絞り込み直せる -->
    <div v-if="selection.filterTags.length" class="filter-tags">
      <span
        v-for="t in selection.filterTags"
        :key="t.key"
        class="filter-tag"
        :title="`${t.label || '条件'}: ${t.value}`"
      >
        <b v-if="t.label">{{ t.label }}</b>{{ t.value }}
        <button class="filter-tag-x" title="この条件を外す" @click="selection.removeFilter(t.key)">✕</button>
      </span>
      <button
        v-if="selection.filterTags.length > 1"
        class="filter-tag-clear"
        @click="selection.clearAllFilters()"
      >すべて解除</button>
    </div>
    <!-- ヘッダーと行だけを横スクロール対象にする(ツールバー・タグ行は常に固定表示) -->
    <div class="table-scroll">
    <div class="table-head grid-row" :style="gridStyle">
      <span></span>
      <span class="th-sort" @click="toggleSort('judgmentStatus')">状態<i class="sort-mark">{{ sortMark('judgmentStatus') }}</i><span
          class="col-resizer"
          title="ドラッグで列幅を変更 / ダブルクリックで全列を既定に戻す"
          @pointerdown="onResizeStart('status', $event)"
          @pointermove="onResizeMove"
          @pointerup="onResizeEnd"
          @pointercancel="onResizeEnd"
          @dblclick="onResizeDouble"
          @click.stop
        ></span></span>
      <span class="th-sort" @click="toggleSort('sellerId')">セラー<i class="sort-mark">{{ sortMark('sellerId') }}</i><span
          class="col-resizer"
          title="ドラッグで列幅を変更 / ダブルクリックで全列を既定に戻す"
          @pointerdown="onResizeStart('seller', $event)"
          @pointermove="onResizeMove"
          @pointerup="onResizeEnd"
          @pointercancel="onResizeEnd"
          @dblclick="onResizeDouble"
          @click.stop
        ></span></span>
      <span class="th-sort" @click="toggleSort('parentAsin')">親ASIN<i class="sort-mark">{{ sortMark('parentAsin') }}</i><span
          class="col-resizer"
          title="ドラッグで列幅を変更 / ダブルクリックで全列を既定に戻す"
          @pointerdown="onResizeStart('parentAsin', $event)"
          @pointermove="onResizeMove"
          @pointerup="onResizeEnd"
          @pointercancel="onResizeEnd"
          @dblclick="onResizeDouble"
          @click.stop
        ></span></span>
      <span class="th-sort" @click="toggleSort('childAsin')">子ASIN<i class="sort-mark">{{ sortMark('childAsin') }}</i><span
          class="col-resizer"
          title="ドラッグで列幅を変更 / ダブルクリックで全列を既定に戻す"
          @pointerdown="onResizeStart('childAsin', $event)"
          @pointermove="onResizeMove"
          @pointerup="onResizeEnd"
          @pointercancel="onResizeEnd"
          @dblclick="onResizeDouble"
          @click.stop
        ></span></span>
      <span class="th-sort" @click="toggleSort('brand')">ブランド<i class="sort-mark">{{ sortMark('brand') }}</i><span
          class="col-resizer"
          title="ドラッグで列幅を変更 / ダブルクリックで全列を既定に戻す"
          @pointerdown="onResizeStart('brand', $event)"
          @pointermove="onResizeMove"
          @pointerup="onResizeEnd"
          @pointercancel="onResizeEnd"
          @dblclick="onResizeDouble"
          @click.stop
        ></span></span>
      <span class="th-sort" @click="toggleSort('productName')">商品名<i class="sort-mark">{{ sortMark('productName') }}</i><span
          class="col-resizer"
          title="ドラッグで列幅を変更 / ダブルクリックで全列を既定に戻す"
          @pointerdown="onResizeStart('productName', $event)"
          @pointermove="onResizeMove"
          @pointerup="onResizeEnd"
          @pointercancel="onResizeEnd"
          @dblclick="onResizeDouble"
          @click.stop
        ></span></span>
      <span class="th-sort" @click="toggleSort('color')">色<i class="sort-mark">{{ sortMark('color') }}</i><span
          class="col-resizer"
          title="ドラッグで列幅を変更 / ダブルクリックで全列を既定に戻す"
          @pointerdown="onResizeStart('color', $event)"
          @pointermove="onResizeMove"
          @pointerup="onResizeEnd"
          @pointercancel="onResizeEnd"
          @dblclick="onResizeDouble"
          @click.stop
        ></span></span>
      <span class="th-sort" @click="toggleSort('size')">サイズ<i class="sort-mark">{{ sortMark('size') }}</i><span
          class="col-resizer"
          title="ドラッグで列幅を変更 / ダブルクリックで全列を既定に戻す"
          @pointerdown="onResizeStart('size', $event)"
          @pointermove="onResizeMove"
          @pointerup="onResizeEnd"
          @pointercancel="onResizeEnd"
          @dblclick="onResizeDouble"
          @click.stop
        ></span></span>
      <span class="th-sort num" @click="toggleSort('createDate')">登録日<i class="sort-mark">{{ sortMark('createDate') }}</i><span
          class="col-resizer"
          title="ドラッグで列幅を変更 / ダブルクリックで全列を既定に戻す"
          @pointerdown="onResizeStart('createDate', $event)"
          @pointermove="onResizeMove"
          @pointerup="onResizeEnd"
          @pointercancel="onResizeEnd"
          @dblclick="onResizeDouble"
          @click.stop
        ></span></span>
      <span class="th-sort num" @click="toggleSort('updateDate')">更新日<i class="sort-mark">{{ sortMark('updateDate') }}</i><span
          class="col-resizer"
          title="ドラッグで列幅を変更 / ダブルクリックで全列を既定に戻す"
          @pointerdown="onResizeStart('updateDate', $event)"
          @pointermove="onResizeMove"
          @pointerup="onResizeEnd"
          @pointercancel="onResizeEnd"
          @dblclick="onResizeDouble"
          @click.stop
        ></span></span>
      <span class="th-sort num" @click="toggleSort('judgeDate')">審査日<i class="sort-mark">{{ sortMark('judgeDate') }}</i><span
          class="col-resizer"
          title="ドラッグで列幅を変更 / ダブルクリックで全列を既定に戻す"
          @pointerdown="onResizeStart('judgeDate', $event)"
          @pointermove="onResizeMove"
          @pointerup="onResizeEnd"
          @pointercancel="onResizeEnd"
          @dblclick="onResizeDouble"
          @click.stop
        ></span></span>
    </div>
    <div class="table-body">
      <div v-if="isLoading" class="empty-state"><span class="spinner"></span> 読み込み中…</div>
      <div v-else-if="isError" class="empty-state">一覧の取得に失敗しました。</div>
      <div v-else-if="items.length === 0" class="empty-state">条件に一致する商品がありません</div>
      <div
        v-for="(item, idx) in items"
        :key="rowKey(item, idx)"
        class="trow grid-row"
        :style="gridStyle"
        :class="{
          selected: isSelected(item, idx),
          'parent-boundary': isParentBoundary(idx),
          'parent-grouped': isInParentGroup(idx),
        }"
        :title="item.productName ?? ''"
        @click="selectRow(item, idx)"
      >
        <input
          type="checkbox"
          :checked="isChecked(item)"
          @click.stop
          @change="selection.toggleChecked(item.sellerId, item.childAsin)"
        >
        <!-- 状態はアイコンのみ。名称はツールチップで補う(列幅を節約し、
             色と形で瞬時に見分けられるようにする) -->
        <span class="status-cell" :class="meta(item).tone" :title="meta(item).label">
          <StatusIcon :name="meta(item).icon" />
        </span>
        <!-- 【表示はSellerId】検索欄(セラーID)は o.SellerId で絞り込むため、
             表示に SellerIdAlias を使うと「検索した値と違うIDが並ぶ」ことになる。
             VB.NET版の一覧も Binding="{Binding SellerId}" で内部IDを表示していた。
             別名は tooltip で補足する。 -->
        <span
          class="tcell seller dbl"
          :title="`${item.sellerIdAlias ? '別名: ' + formatSellerId(item.sellerIdAlias) + ' / ' : ''}ダブルクリックでこのセラーに絞り込み`"
          @dblclick.stop="filterBySeller(item)"
        >{{ formatSellerId(item.sellerId) }}</span>
        <span class="tcell mono dbl" title="ダブルクリックでこの親ASINに絞り込み" @dblclick.stop="filterByParentAsin(item)">{{ item.parentAsin }}</span>
        <span class="tcell mono dbl" title="ダブルクリックでこの子ASINに絞り込み" @dblclick.stop="filterByChildAsin(item)">{{ item.childAsin }}</span>
        <span class="tcell brand dbl" title="ダブルクリックでこのブランドに絞り込み" @dblclick.stop="filterByBrand(item)">{{ item.brand }}</span>
        <span class="tcell name">{{ item.productName }}</span>
        <span class="tcell small">{{ item.color }}</span>
        <span class="tcell small">{{ item.size }}</span>
        <span
          class="tcell date dbl"
          :title="`${item.createDate ?? ''}\nダブルクリックでこの日付に絞り込み`"
          @dblclick.stop="filterByCreateDate(item)"
        >{{ shortDate(item.createDate) }}</span>
        <span class="tcell date" :title="item.updateDate ?? ''">{{ shortDate(item.updateDate) }}</span>
        <span class="tcell date" :title="item.judgeDate ?? ''">{{ shortDate(item.judgeDate) }}</span>
      </div>
    </div>
    </div>
  </div>
</template>
